/**
 * RescheduleModal Component
 * ---------------------------------------------------------
 * Allows the user to reschedule a follow-up task by selecting
 * a new date/time and optionally adding a note.
 * 
 * Key Features:
 * - Validates date selection
 * - Adds a note (optional)
 * - Reschedules the task via Firebase
 * - Shows loader and uses styled buttons
 */
import { FunctionComponent, useState, useRef } from "react";
import { addNoteFirebase, reScheduleTaskFirebase } from "../../../Services/contacts";
import { connect, useDispatch } from "react-redux";
import { showSnackbarAction } from "../../../Redux/actions";
import { useHistory } from "react-router-dom";
import moment from "moment";
import TextInput from "../../TextInput/InputText";
import { themeColors } from "../../theme";
import "../../../theme-vars.css";
import { Box, Grid, useMediaQuery, useTheme } from "@mui/material";
import Modal, { Styles } from "react-modal";
import StyledCancelButton from "../../Buttons/StyledCancelButton";
import StyledSubmitButton from "../../Buttons/StyledSubmitButton";
import { Circles } from "react-loader-spinner";
import InputTextDate from "../../TextInput/InputTextDate";

type props = {
  contactDetail: any;
  close: () => void;
  open: boolean;
  tasksData: any[];
  notes: any[];
  user: any;
  theme: any;
};

const RescheduleModal: FunctionComponent<props> = ({
  contactDetail,
  close,
  open,
  tasksData,
  notes,
  user,
  theme
}) => {
  const themeMUI = useTheme();
  const isMobile = useMediaQuery(themeMUI.breakpoints.down("sm"));
  const dispatcher = useDispatch();
  const history = useHistory();
  const noteRef: any = useRef();
  const [load, setLoad] = useState(false);
  const [nextFollowDate, setNextFollowDate] = useState<any>('');
  const [nextFollowSelected, setNextFollowSelected] = useState<any>({ label: contactDetail.next_follow_up_type ? contactDetail.next_follow_up_type : "Select", value: contactDetail.next_follow_up_type ? contactDetail.next_follow_up_type : "Select" });

   /**
   * Validate fields and submit the new scheduled time.
   */
  const onSubmit = () => {
    if (nextFollowDate === "" || nextFollowDate < new Date()) {
      dispatcher(
        showSnackbarAction("Select A Valid Date!!", "error")
      );
    } else {
      setLoad(true);

       // Add note if user entered one
      if (noteRef.current.value.length !== 0) {
        addNoteFirebase(
          contactDetail.contactId,
          notes,
          noteRef.current.value,
          () => { },
          dispatcher,
          () => { },
          user.user_email
        );
      }

      // Submit new follow-up date
      reScheduleTaskFirebase(
        moment(nextFollowDate).toDate(),
        tasksData,
        contactDetail.contactId,
        dispatcher,
        history
      );
    }
  };

   /**
   * Modal styling dynamically adjusted based on theme + device size
   */
  const modalStyle: Styles = {
    overlay: {
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      zIndex: 1300,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    content: {
      background: theme
        ? themeColors.cardBackgroundColor
        : themeColors.cardBackgroundColorDark,
      color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark,
      borderRadius: 16,
      padding: isMobile ? "24px 12px" : "40px 32px",
      width: 400,
      maxWidth: 400,
      border: "none",
      boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      position: "relative",
      inset: "auto",
      margin: "auto",
    },
  };

  return (
    <>
      <Modal
        isOpen={open}
        onRequestClose={close}
        shouldCloseOnOverlayClick={true}
        shouldCloseOnEsc={true}
        style={modalStyle}
        ariaHideApp={false}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 380,
            mx: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <form style={{ width: "100%" }}>
              {/* Header */}
            <Box sx={{ mb: 2 }}>
              <h3
                style={{
                  color: theme ? "#272944" : "#fff",
                  fontFamily: "var(--font-family-main)",
                  fontSize: "var(--font-size-large)",
                  marginBottom: 24,
                  textAlign: "center",
                  fontWeight: 700,
                  letterSpacing: 0.5,
                }}
              >
                Reschedule Task
              </h3>
            </Box>
             {/* Inputs */}
            <Grid container spacing={2} sx={{ width: "100%" }}>
              <Grid item xs={12} sm={12}>
                <InputTextDate
                  type="datetime-local"
                  title="Next Follow Up Date & Time"
                  value={nextFollowDate}
                  onChange={(e: any) => setNextFollowDate(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={12}>
                <TextInput
                  title="Enter Note"
                  style={{ backgroundColor: "#fff" }}
                  ref={noteRef}
                />
              </Grid>
            </Grid>

            {/* Buttons */}
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mt: 4,
                  width: "100%",
                }}
              >
                <Box style={{ marginRight: 8 }}>
                  <StyledCancelButton onClick={close} isMobile={isMobile}>
                    Cancel
                  </StyledCancelButton>
                </Box>
                <Box>
                  <StyledSubmitButton
                    onClick={onSubmit}
                    themeMode={themeMUI.palette.mode}
                    isMobile={isMobile}
                  >
                    {load ? (
                      <span style={{ display: "flex", justifyContent: "center" }}>
                        <Circles color="#fff" height={20} width={20} />
                      </span>
                    ) : (
                      <>Submit</>
                    )}
                  </StyledSubmitButton>
                </Box>
              </Box>
            </Grid>
          </form>
        </Box>
      </Modal >
    </>
  );
};

/* Redux Mapping */
const mapStateToProps = (state: any) => {
  return {
    user: state.user.data,
    theme: state.theme.isLightMode
  };
};

export default connect(mapStateToProps)(RescheduleModal);
